import type { ShallowDehydrateObject } from 'kysely'
import { z } from 'zod'

import type {
  CableId,
  LoggerActivationId,
  LoggerId,
  ServiceItemId,
} from '@beaded/models'

import type { UserId } from '../../index.js'
import type {
  Order,
  OrderInitializer,
  OrderMutator,
} from '../schemas/public/Order.js'
import {
  order,
  orderInitializer,
  orderMutator,
} from '../schemas/public/Order.js'
import type {
  OrderLineItem,
  OrderLineItemInitializer,
  OrderLineItemMutator,
} from '../schemas/public/OrderLineItem.js'
import {
  orderLineItem,
  orderLineItemInitializer,
  orderLineItemMutator,
} from '../schemas/public/OrderLineItem.js'

export type ExtendedOrderInitializer = OrderInitializer & {
  items: OrderLineItemInitializer[]
}

export const extendedOrderInitializer = z.object({
  items: z.array(orderLineItemInitializer),
  ...(orderInitializer as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<ExtendedOrderInitializer>

export type ExtendedOrderMutator = OrderMutator & {
  items: OrderLineItemMutator[]
}

export const extendedOrderMutator = z.object({
  items: z.array(orderLineItemMutator),
  ...(orderMutator as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<ExtendedOrderMutator>

export type ExtendedOrder = Omit<
  Order,
  | 'updatedAt'
  | 'updatedBy'
  | 'deletedAt'
  | 'deletedBy'
  | 'notes'
  | 'currency'
  | 'totalPriceDollars'
> & {
  items: ExtendedOrderLineItem[]
  organizationName: string
  projectName: string
  createdBy: string
  createdAt: Date | string
  updatedAt: Date | string | null
  createdByUser: {
    id: UserId
    email: string
    nameFirst: string
    nameLast: string
  }
}

// Embedded via kysely's jsonArrayFrom/jsonObjectFrom: postgres json_agg
// serializes timestamps to ISO strings and node-postgres does not revive them,
// so date columns arrive as strings. ShallowDehydrateObject is the same
// transformation the query builder applies, so these types cannot drift from
// the real row shape.
export type ExtendedOrderLineItem = ShallowDehydrateObject<
  Omit<
    OrderLineItem,
    | 'updatedBy'
    | 'createdBy'
    | 'deletedAt'
    | 'deletedBy'
    | 'lineItemDescription'
  >
> & {
  rentalEndDate?: Date | string | null
  loggers?: {
    loggerSerial?: string
    loggerId?: LoggerId
    siteId?: number | null
    siteName?: string | null
  }[]
  serviceItems?: {
    serviceItemId: ServiceItemId
    amount?: number | null
    daysPurchased?: number | null
    daysConsumed?: number | null
    expiredAt?: Date | string | null
    nextServiceItemId?: string | null
    loggerActivations?: Array<{
      loggerActivationId: LoggerActivationId
      loggerId: LoggerId
      loggerSerial: string
      activatedAt: Date | string
      suspendedAt?: Date | string | null
    }>
  }[]
  cables: Array<{
    cableId: CableId
    cableSerial: number | null
    cableName: string
    parentCableId: CableId | null
  }>
}

export const extendedOrder = z.object({
  items: z.array(orderLineItem),
  ...(order as unknown as z.ZodObject<any>).shape,
}) as unknown as z.Schema<ExtendedOrder>
